import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filter, find, map, omitBy, isUndefined, isEmpty } from 'lodash';
import errorHandler from 'Utils/error-handler';

import { ArrowRightOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { AlertBox, Button, SelectInput, TextInput } from '@life-line-lab/components-react';

import country from 'Data/country.json';
import state from 'Data/state.json';
import statesCities from 'Components/profile/profile-details/addresses/country_MY.json'

import PrimaryAddressFormSchema from './address-info-form.validation';

interface Input {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  businessName: string;
  registrationNo: string;
}

interface PrimaryAddressFormProps {
  accountType: "BUSINESS_ACCOUNT" | "INDIVIDUAL_ACCOUNT";
  status?: string;
  data?: any;
  resError?: any;
  onBack: () => void;
  onSubmit: (input: any) => void;
}

const PrimaryAddressForm: React.FC<PrimaryAddressFormProps> = ({ status, data, resError, onBack, onSubmit, accountType }) => {
  const initialInput = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'MY',
    businessName: '',
    registrationNo: '',
    accountType: accountType,
  }

  const elRef = React.useRef<HTMLHeadingElement>(null);
  const initialError = {};
  const initialStateList = map((filter(state, (o: any) => o.CountryCode === 'MY')),
    (o: any) => { return { value: o.Code, label: o.Name }}
  );

  const { t } = useTranslation();

  const [input, setInput] = useState<Input>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [stateList, setStateList] = useState<any[]>(initialStateList);
  const [cityList, setCityList] = useState<any[]>([]);

  const scrollToRef = (ref: any) => ref.current.scrollIntoView(false);

  useEffect(() => {
    if (!isEmpty(data))
      setInput({ ...input, ...data });
  }, [data])

  useEffect(() => {
    if (status === 'error' && !isEmpty(resError)) {
      if (resError.type === 'VALIDATION_ERROR') {

        if (resError.stack?.field) {
          const value = resError.value;
          const field = resError.stack.field;

          setError({ ...error, [field]: t(`${value}`, { field: t(`label.${field}`) }) });
          (document.querySelector(`input[name=${resError.stack.field}]`) as HTMLInputElement)?.focus();
        }
        else {
          setError({ ...error, general: t(resError.value) });
        }
      }
      else {
        setError({ ...error, general: t(resError.value) });
        scrollToRef(elRef);
      }
    }
  }, [status, resError])

  useEffect(() => {
    const selected = find(country, (o: any) => o.Code === input.country); console.log(selected)
    const stateList = map((filter(state, (o: any) => o.CountryCode === selected?.Code)),
      (o: any) => { return { value: o.Code, label: o.Name }}
    );
    setStateList(stateList);
  }, [input.country])

  useEffect(() => {
    const filteredCityList = statesCities.STATES.find(s => s.CODE === input.state);
    if (!filteredCityList) {
      setCityList([]);
      setInput(prevInput => ({
        ...prevInput,
        city: '',
      }))
      return;
    }

    setCityList(filteredCityList.CITIES.map(c => ({
      value: c.CODE,
      label: c.NAME,
    })));
  }, [input.state])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  }

  const handleSelectChange = (value: any, name: string) => {
    setError(initialError);

    if (name === 'country') {
      const selected = find(country, (o: any) => o.Code === value);
      const stateList = map((filter(state, (o: any) => o.CountryCode === selected?.Code)),
        (o: any) => { return { value: o.CODE, label: o.NAME }}
      );
      setStateList(stateList);
      setInput({ ...input, [name]: value, state: '' });
    }
    else {
      setInput({ ...input, [name]: value });
    }
  }

  const handleSubmit = () => {
    try {
      const value = PrimaryAddressFormSchema.validateSync(omitBy({
        ...input
      }, isUndefined), {
        strict: true,
        abortEarly: true,
        stripUnknown: false
      });

      if (!isEmpty(value))
        onSubmit(value);
    }
    catch(err) {
      let { path, value }: any = errorHandler.validation(err);

      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) })
      });

      if (document.querySelector(`input[name=${path}]`)) {
        (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
      }
      else if (document.querySelector(`div[name=${path}]`)) {
        (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
      }
    }
  }

  useEffect(() => {
    if (status === 'error' && !isEmpty(resError)) {
      setError({ ...error, general: t(resError.value) });
    }
  }, [status]);

  return (
    <div className="primary-address-form">
      <Space direction="vertical" size="large">
        <div ref={elRef}>
          <AlertBox isFilled={true} text={error.general} />
        </div>

        {
          accountType === 'BUSINESS_ACCOUNT' &&
          <>
            <div>
              <TextInput
                isError={!!error.businessName}
                isRequired={true}
                label={t('label.businessName')}
                name="businessName"
                value={input.businessName}
                onChange={handleChange}
              />
              <AlertBox text={error.businessName} />
            </div>

            <div>
              <TextInput
                isError={!!error.registrationNo}
                label={t('label.registrationNo')}
                name="registrationNo"
                value={input.registrationNo}
                onChange={handleChange}
              />
              <AlertBox text={error.registrationNo} />
            </div>
          </>
        }
        <div>
          <TextInput
            isError={!!error.addressLine1}
            isRequired={true}
            label={t('label.addressLine1')}
            name="addressLine1"
            value={input.addressLine1}
            onChange={handleChange}
          />
          <AlertBox text={error.addressLine1} />
        </div>

        <div>
          <TextInput
            isError={!!error.addressLine2}
            label={t('label.addressLine2')}
            name="addressLine2"
            value={input.addressLine2}
            onChange={handleChange}
          />
          <AlertBox text={error.addressLine2} />
        </div>

        <div>
          <TextInput
            isError={!!error.postcode}
            isRequired={true}
            label={t('label.postcode')}
            name="postcode"
            value={input.postcode}
            onChange={handleChange}
          />
          <AlertBox text={error.postcode} />
        </div>

        <div>
          <SelectInput
            isError={!!error.country}
            label={t('label.country')}
            name="country"
            value={input.country}
            showSearch={true}
            placeholder={t('option.selectOne')}
            onChange={
              (value: string | string[]) =>
                handleSelectChange(value, 'country')
            }
            filterOption={(input: string, option: any) =>
              option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
              option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            isRequired={true}
            options={map(country, (o) => {
              return {
                value: o.Code,
                label: o.Name
              }
            })}
          />
          <AlertBox text={error.country} />
        </div>

        <div>
          <SelectInput
            isError={!!error.state}
            label={t('label.state')}
            name="state"
            value={input.state}
            showSearch={true}
            placeholder={t('option.selectOne')}
            onChange={
              (value: string | string[]) =>
                handleSelectChange(value, 'state')
            }
            filterOption={(input: string, option: any) =>
              option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
              option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            isRequired={true}
            options={stateList}
          />
          <AlertBox text={error.state} />
        </div>

        <div>
          <SelectInput
            isError={!!error.city}
            label={t('label.city')}
            name="city"
            value={input.city}
            showSearch={true}
            placeholder={t('option.selectOne')}
            onChange={
              (value: string | string[]) =>
                handleSelectChange(value, 'city')
            }
            filterOption={(input: string, option: any) =>
              option.props.value.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
              option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            isRequired={input.country === 'MY'}
            options={cityList}
          />
          <AlertBox text={error.city} />
        </div>

        <div className="form--actions center">
          <Button
            className="btn--ghost mr-10"
            type="primary"
            label={t('action.back')}
            size="large"
            onClick={status === "loading" ? () => {} : onBack}
          />

          <Button
            isLoading={status === "loading"}
            type="primary"
            label={
              <React.Fragment>
                <span>{t('action.register')}</span> <ArrowRightOutlined />
              </React.Fragment>
            }
            size="large"
            onClick={handleSubmit}
          />
        </div>
      </Space>
    </div>
  )
}

export default PrimaryAddressForm;